import { CheckOutlined } from "@ant-design/icons";
import { Alert, Button, Flex, Form, Input, Typography } from "antd";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { AuthLayout } from "@/components/AuthLayout";
import { useI18n } from "@/i18n";

type Values = { name: string; email: string; password: string };

// Worded as the open beta perks on zerpid.com (#beta), so the promise does not change between the two pages.
const PERKS = ["auth.register.perk1", "auth.register.perk2", "auth.register.perk3", "auth.register.perk4", "auth.register.perk5"] as const;

export function Register() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  // A beta invitation links here with the invited address.
  const [params] = useSearchParams();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (values: Values) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await unwrap(api.POST("/v1/auth/register", { body: { ...values, locale } }));
      navigate(`/verify?email=${encodeURIComponent(res.email)}&sent=1`, { replace: true });
    } catch (err) {
      if (!applyFieldErrors(form, err)) setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const taken = error instanceof ApiError && error.code === "email_taken";

  return (
    <AuthLayout
      title={t("auth.register.title")}
      subtitle={t("auth.register.subtitle")}
      aside={
        <section style={{ width: "100%", maxWidth: 360 }}>
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {t("auth.register.perksTitle")}
          </Typography.Title>
          <Flex vertical gap={12}>
            {PERKS.map((key) => (
              <Flex key={key} gap={10} align="baseline">
                <CheckOutlined style={{ color: "var(--z-brand)" }} />
                <Typography.Text>{t(key)}</Typography.Text>
              </Flex>
            ))}
          </Flex>
        </section>
      }
    >
      {error !== null && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title={errorText(error)}
          description={taken ? <Link to="/login">{t("auth.register.emailTakenAction")}</Link> : undefined}
        />
      )}
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting} initialValues={{ email: params.get("email") ?? "" }}>
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, message: t("field.required") }, { max: 120, message: t("field.too_long") }]}>
          <Input autoComplete="name" autoFocus />
        </Form.Item>
        <Form.Item name="email" label={t("common.email")} rules={[{ required: true, type: "email", message: t("field.invalid_email") }]}>
          <Input autoComplete="email" inputMode="email" />
        </Form.Item>
        <Form.Item
          name="password"
          label={t("common.password")}
          extra={t("field.password_hint")}
          rules={[{ required: true, min: 10, message: t("field.password_hint") }, { max: 128, message: t("field.too_long") }]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={submitting}>
          {t("auth.register.submit")}
        </Button>
      </Form>
      <Typography.Paragraph style={{ marginTop: 24, marginBottom: 0, textAlign: "center" }}>
        {t("auth.register.haveAccount")} <Link to="/login">{t("auth.register.login")}</Link>
      </Typography.Paragraph>
    </AuthLayout>
  );
}
